import React, { useCallback, useEffect, useRef, useState } from 'react'
import Table, { Column, TableHandle } from '../../../components/Table';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { setFiletrData, user } from '../../../redux/slices/Users/slice';
import { Button } from '../../../components/Button';
import { FaEdit, FaTrash, FaExclamationTriangle } from 'react-icons/fa'; // 🚨 Added FaTrash & FaExclamationTriangle
import { LightColors } from '../../../utils/colors/colors';
import { useNavigate } from 'react-router-dom';
import { fetchUserList } from '../../../redux/slices/Users/thunk';
import FilterForm from './FilterForm';
import Checkbox from '../../../components/Checkbox';
import { apiDelete } from '../../../services/apiServices';
import { endPoints } from '../../../configs/apiUrls';
import { toast } from 'react-toastify';

const ValuerList = () => {
    const dispatch: any = useDispatch()
    const tableRef = useRef<TableHandle>(null);
    const navigate = useNavigate();
    const {
        usersList,
        filterData,
        loading,
    } = useSelector((state: RootState) => state.users)

    const [refresh, setRefresh] = useState(false)

    // --- MODAL STATE ---
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        userId: "",
        userName: ""
    });

    // --- DELETE HANDLERS ---
    const initiateDelete = (id: string, name: string) => {
        setDeleteModal({ isOpen: true, userId: id, userName: name });
    };

    const cancelDelete = () => {
        setDeleteModal({ isOpen: false, userId: "", userName: "" });
    };

    const confirmDelete = async () => {
        try {
            const res: any = await apiDelete(`${endPoints.DELETE_USER}/${deleteModal.userId}`);

            if (res.success) {
                toast.success(`User ${deleteModal.userName} deleted successfully`);
                setRefresh(!refresh);
            } else {
                toast.error(res.message || "Failed to delete user");
            }
        } catch (error) {
            console.error("Delete user error:", error);
            toast.error("An error occurred while deleting the user");
        } finally {
            setDeleteModal({ isOpen: false, userId: "", userName: "" });
        }
    };

    const calculateTotalExperience = (baseExpYears: any, joinDateString: string) => {
        // If no base experience is provided, return fallback
        if (baseExpYears === undefined || baseExpYears === null || isNaN(Number(baseExpYears))) {
            return 'Not provided';
        }

        const baseExp = Number(baseExpYears);
        if (!joinDateString) return `${baseExp} Years`;

        const joinDate = new Date(joinDateString);
        const today = new Date();

        // Calculate difference in years and months
        let yearsDiff = today.getFullYear() - joinDate.getFullYear();
        let monthsDiff = today.getMonth() - joinDate.getMonth();

        // Adjust if the current month is earlier in the year than the join month
        if (monthsDiff < 0) {
            yearsDiff -= 1;
            monthsDiff += 12;
        }

        // Prevent negative elapsed time if the join date is somehow in the future
        if (yearsDiff < 0) {
            yearsDiff = 0;
            monthsDiff = 0;
        }

        const totalYears = baseExp + yearsDiff;
        const totalMonths = monthsDiff;

        // Format string cleanly
        let result = `${totalYears} Year${totalYears !== 1 ? 's' : ''}`;
        if (totalMonths > 0) {
            result += ` ${totalMonths} Month${totalMonths !== 1 ? 's' : ''}`;
        }

        return result;
    };

    // Table columns for User
    const columns: Column<user | any>[] = [
        { key: "userType", label: "User Type", sortable: true, },
        { key: "name", label: "Name", sortable: true },
        { key: "mobileNumber", label: "Mobile Number" },
        { key: "state", label: "State" },
        { key: "city", label: "City" },
        {
            key: "area", label: "Area",
            customCell: (row) => (
                <div>
                    {row?.area?.area || row?.area?.name || "-"}
                </div>
            )
        },
        {
            key: "experience", label: "Experience",
            customCell: (row) => (
                <div className='flex justify-center items-center'>
                    {calculateTotalExperience(row?.experience, row?.createdAt)}
                </div>
            )
        },
        // Overall Rating Column
        {
            key: "overallRating",
            label: "Overall Rating",
            sortable: true,
            customCell: (row) => (
                <div className='flex items-center gap-1'>
                    <span className={`text-lg ${row.overallRating > 0 ? "text-yellow-500" : "text-gray-300"}`}>★</span>
                    <span className="font-semibold text-sm">
                        {row.overallRating > 0 ? row.overallRating : "N/A"}
                    </span>
                    {row.totalReviews > 0 && (
                        <span className="text-xs text-gray-500 ml-1">({row.totalReviews})</span>
                    )}
                </div>
            )
        },
        {
            key: "isVerified",
            label: "OTP Verified",
            customCell: (row) => (
                <div className='flex justify-center items-center'>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${row.isVerified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {row.isVerified ? "Yes" : "No"}
                    </span>
                </div>
            )
        },
        {
            key: "isApproved", label: "Approved",
            customCell: (row) => (
                <div className='flex justify-center items-center'>
                    <Checkbox
                        checked={row.isApproved ? true : false}
                        onCheckedChange={function (checked: boolean) {
                            // Action for toggle
                        }}
                    />
                </div>)

        },
        {
            key: "action",
            label: "Actions",
            sortable: false,
            customCell: (row) => (
                <div className='flex justify-center items-center gap-4'>
                    <a
                        className="text-blue-600 cursor-pointer transition-transform hover:scale-110"
                        title="Edit User"
                        onClick={() => navigate(`/users/edit/${row._id}`, { state: { row } })}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/users/edit/${row._id}`, { state: { row } }); }}
                    >
                        <FaEdit color={LightColors.accent} size={18} />
                    </a>

                    <a
                        className="text-red-500 cursor-pointer transition-transform hover:scale-110"
                        title="Delete User"
                        onClick={() => initiateDelete(row.userId, row.name)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") initiateDelete(row._id, row.name); }}
                    >
                        <FaTrash size={16} />
                    </a>
                </div>
            ),
        },
    ];

    const onSortChanged = (sortBy: any, sortOrder: any) => {
        dispatch(setFiletrData({
            ...filterData,
            sortBy: sortBy,
            sortOrder: sortOrder
        }))
    };

    useEffect(() => {
        dispatch(fetchUserList({
            ...filterData,
            userType: "valuer"
        }));
    }, [
        filterData.page,
        filterData.limit,
        filterData.area,
        filterData.city,
        filterData.state,
        filterData.search,
        filterData.sortBy,
        filterData.sortOrder,
        refresh
    ]);

    return (
        <div>
            <Table
                ref={tableRef}
                columns={columns as any}
                data={usersList?.length ? usersList : []}
                tableName="Valuers"
                totalCount={filterData.totalCount}
                currentPage={filterData.page}
                pageSize={filterData.limit}
                loading={loading.usersList}
                filterContent={<FilterForm tableRef={tableRef} />}
                onSearchChange={(searchText) => {
                    dispatch(setFiletrData({ ...filterData, search: searchText, page: 1 }));
                }}
                onPageChange={(page) => {
                    dispatch(setFiletrData({ ...filterData, page: page }))
                }}
                onPageSizeChange={function (size: number): void {
                    dispatch(setFiletrData({ ...filterData, limit: size, page: 1 }))
                }}
                renderRightToolItems={
                    <div className='flex gap-2'>
                        <Button
                            variant="primary"
                            onClick={() => setRefresh(!refresh)}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                navigate("/users/add")
                            }}
                        >
                            Add
                        </Button>
                    </div>
                }
                onSortChange={onSortChanged}
            />

            {/* --- CONFIRMATION MODAL --- */}
            {deleteModal.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 transform transition-all">
                        <div className="flex items-center justify-center mb-4 text-red-500">
                            <FaExclamationTriangle size={40} />
                        </div>
                        <h3 className="text-xl font-bold text-center text-gray-800 mb-2">
                            Confirm Deletion
                        </h3>
                        <p className="text-center text-gray-600 mb-6">
                            Are you sure you want to delete user <span className="font-semibold text-gray-800">"{deleteModal.userName}"</span>?
                            This action cannot be undone.
                        </p>

                        <div className="flex justify-center gap-4">
                            <button
                                onClick={cancelDelete}
                                className="px-6 py-2 rounded-md border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-6 py-2 rounded-md bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
                            >
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ValuerList